import { useState, useEffect } from 'preact/hooks';
import { MainLayout } from '../../components/MainLayout';
import { getAuthHeaders } from '../../utils/auth';

interface Team {
  teamId: string;
  name: string;
  description?: string;
  memberCount: number;
  role: 'admin' | 'member';
  status: 'active' | 'deactivated';
}

interface TeamMember {
  userId: string;
  email: string;
  name: string;
  role: 'admin' | 'member';
  status: 'active' | 'pending' | 'suspended';
  joinedAt: string | null;
  lastActiveAt?: string;
}

interface TeamMembersPageProps {
  teamId: string;
}

export function TeamMembersPage({ teamId }: TeamMembersPageProps) {
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadTeamDetails();
    loadTeamMembers();
    loadTeamInvitations();
  }, [teamId]);

  const loadTeamDetails = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load team details');
      }

      const data = await response.json();
      setTeam(data.team);
    } catch (err) {
      console.error('Error loading team details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team');
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load team members');
      }

      const data = await response.json();
      // Flatten user data for easier access
      const flattenedMembers = (data.members || []).map((member: any) => ({
        ...member,
        name: member.user?.name || member.user?.email || 'Unknown User',
        email: member.user?.email || 'No email'
      }));
      setMembers(flattenedMembers);
    } catch (err) {
      console.error('Error loading team members:', err);
      // Don't show error if API doesn't exist yet
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamInvitations = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/invitations`, {
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Only show pending invitations
        const pendingInvitations = (data.invitations || []).filter((inv: any) => inv.status === 'pending');
        setInvitations(pendingInvitations);
      }
    } catch (err) {
      console.error('Error loading team invitations:', err);
    }
  };

  const handleChangeRole = async (userId: string, newRole: 'admin' | 'member') => {
    setActionLoading(userId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders())
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) {
        throw new Error('Failed to update member role');
      }

      setSuccess(`Role updated to ${newRole}`);
      await loadTeamMembers();
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveMember = async (userId: string, memberName: string) => {
    if (!confirm(`Remove ${memberName} from the team?`)) {
      return;
    }

    setActionLoading(userId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members/${userId}`, {
        method: 'DELETE',
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove member');
      }

      setSuccess(`${memberName} has been removed from the team`);
      await loadTeamMembers();
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspendMember = async (userId: string, memberName: string) => {
    if (!confirm(`Suspend ${memberName}? They will lose access to the team.`)) {
      return;
    }

    setActionLoading(userId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/members/${userId}/suspend`, {
        method: 'POST',
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to suspend member');
      }

      setSuccess(`${memberName} has been suspended`);
      await loadTeamMembers();
    } catch (err) {
      console.error('Error suspending member:', err);
      setError(err instanceof Error ? err.message : 'Failed to suspend member');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInvitation = async (invitationId: string, email: string) => {
    if (!confirm(`Cancel invitation for ${email}?`)) {
      return;
    }

    setActionLoading(invitationId);
    setError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/teams/${teamId}/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          ...(await getAuthHeaders())
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel invitation');
      }

      setSuccess(`Invitation to ${email} has been canceled`);
      await loadTeamInvitations();
    } catch (err) {
      console.error('Error canceling invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleIcon = (role: string) => {
    return role === 'admin' ? '' : '';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span class="badge badge-success badge-sm">Active</span>;
      case 'pending':
        return <span class="badge badge-warning badge-sm">Pending</span>;
      case 'suspended':
        return <span class="badge badge-error badge-sm">Suspended</span>;
      default:
        return <span class="badge badge-ghost badge-sm">{status}</span>;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div class="container mx-auto p-8 max-w-6xl">
            <div class="flex items-center justify-center h-64">
              <span class="loading loading-spinner loading-lg"></span>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error && !team) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div class="container mx-auto p-8 max-w-6xl">
            <div class="alert alert-error mb-4">
              <span>{error}</span>
            </div>
            <a href="/teams" class="btn btn-ghost">← Back to Teams</a>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!team) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div class="container mx-auto p-8 max-w-6xl">
            <div class="alert alert-error mb-4">
              <span>Team not found</span>
            </div>
            <a href="/teams" class="btn btn-ghost">← Back to Teams</a>
          </div>
        </div>
      </MainLayout>
    );
  }

  const canManage = team.role === 'admin';

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">
        <div class="container mx-auto p-8 max-w-6xl">
        {/* Header */}
        <div class="flex items-center gap-4 mb-8">
          <a href={`/teams/${teamId}`} class="btn btn-ghost btn-sm">← Back to Team</a>
          <div class="flex-1">
            <h1 class="text-3xl font-bold">Team Members</h1>
            <p class="text-base-content/70 mt-2">
              Manage members of {team.name}
            </p>
          </div>
          {canManage && (
            <a href={`/teams/${teamId}/invite`} class="btn btn-primary">
              + Invite Members
            </a>
          )}
        </div>

        {/* Status Messages */}
        {error && (
          <div class="alert alert-error mb-6">
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div class="alert alert-success mb-6">
            <span>{success}</span>
          </div>
        )}

        {/* Members List */}
        <div class="card bg-base-100 shadow-md">
          <div class="card-body">
            <div class="flex justify-between items-center mb-6">
              <h2 class="card-title">All Members ({members.length + invitations.length})</h2>
              <div class="flex gap-2">
                <select class="select select-bordered select-sm">
                  <option>All Roles</option>
                  <option>Admins</option>
                  <option>Members</option>
                </select>
                <select class="select select-bordered select-sm">
                  <option>All Status</option>
                  <option>Active</option>
                  <option>Pending</option>
                  <option>Suspended</option>
                </select>
              </div>
            </div>

            {members.length === 0 && invitations.length === 0 ? (
              <div class="text-center py-12">
                <div class="text-6xl mb-4">👥</div>
                <h3 class="text-xl font-semibold mb-2">No members found</h3>
                <p class="text-base-content/70 mb-6">
                  Start building your team by inviting members
                </p>
                {canManage && (
                  <a href={`/teams/${teamId}/invite`} class="btn btn-primary">
                    Invite First Member
                  </a>
                )}
              </div>
            ) : (
              <div class="overflow-x-auto">
                <table class="table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Joined</th>
                      <th>Last Active</th>
                      {canManage && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.userId}>
                        <td>
                          <div class="flex items-center gap-3">
                            <div class="avatar placeholder">
                              <div class="bg-neutral text-neutral-content rounded-full w-8 h-8">
                                <span class="text-xs">{member.name.charAt(0).toUpperCase()}</span>
                              </div>
                            </div>
                            <div>
                              <div class="font-semibold">{member.name}</div>
                              <div class="text-sm text-base-content/70">{member.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div class="flex items-center gap-2">
                            {getRoleIcon(member.role)}
                            <span class="capitalize">{member.role}</span>
                          </div>
                        </td>
                        <td>{getStatusBadge(member.status)}</td>
                        <td>
                          {member.joinedAt
                            ? new Date(member.joinedAt).toLocaleDateString()
                            : '-'
                          }
                        </td>
                        <td>
                          {member.lastActiveAt
                            ? new Date(member.lastActiveAt).toLocaleDateString()
                            : 'Never'
                          }
                        </td>
                        {canManage && (
                          <td>
                            <div class="dropdown dropdown-end">
                              <button
                                class={`btn btn-ghost btn-xs ${actionLoading === member.userId ? 'loading' : ''}`}
                                disabled={actionLoading === member.userId}
                              >
                                {actionLoading === member.userId ? '' : '⋮'}
                              </button>
                              <ul class="dropdown-content menu bg-base-100 rounded-box z-[1] w-52 p-2 shadow">
                                <li>
                                  <button onClick={() => handleChangeRole(member.userId, member.role === 'admin' ? 'member' : 'admin')}>
                                    {member.role === 'admin' ? 'Make Member' : 'Make Admin'}
                                  </button>
                                </li>
                                {member.status === 'active' && (
                                  <li>
                                    <button onClick={() => handleSuspendMember(member.userId, member.name)}>
                                      Suspend Member
                                    </button>
                                  </li>
                                )}
                                <li>
                                  <button
                                    class="text-error"
                                    onClick={() => handleRemoveMember(member.userId, member.name)}
                                  >
                                    Remove Member
                                  </button>
                                </li>
                              </ul>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {invitations.map((invitation) => (
                      <tr key={`inv-${invitation.invitationId}`} class="opacity-75">
                        <td>
                          <div class="flex items-center gap-3">
                            <div class="avatar placeholder">
                              <div class="bg-warning text-warning-content rounded-full w-8 h-8">
                                <span class="text-xs">📧</span>
                              </div>
                            </div>
                            <div>
                              <div class="font-semibold">{invitation.email}</div>
                              <div class="text-sm text-base-content/70">Invitation pending</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div class="flex items-center gap-2">
                            {getRoleIcon(invitation.role)}
                            <span class="capitalize">{invitation.role}</span>
                          </div>
                        </td>
                        <td>{getStatusBadge(invitation.status)}</td>
                        <td>{new Date(invitation.createdAt).toLocaleDateString()}</td>
                        <td>
                          <span class="text-sm text-base-content/70">
                            Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                          </span>
                        </td>
                        {canManage && (
                          <td>
                            <button
                              class="btn btn-ghost btn-xs text-error"
                              onClick={() => handleCancelInvitation(invitation.invitationId, invitation.email)}
                              disabled={actionLoading === invitation.invitationId}
                            >
                              {actionLoading === invitation.invitationId ? '...' : 'Cancel'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
    </MainLayout>
  );
}
